<script setup lang="ts">
import { ref, computed, onMounted, watch, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSession } from '../auth';
import { trpc } from '../trpc';
import AppHeader from '@/components/layout/AppHeader.vue';
import { Button } from '@/components/ui/button';

interface Deck {
  id: string;
  name: string;
  userId: string;
  isDefault: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Flashcard {
  id: string;
  userId: string;
  deckId: string;
  question: string;
  answer: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const router = useRouter();
const route = useRoute();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const isLoading = computed(() => session.value?.isPending);

const deckId = computed(() => route.params.id as string);
const deck = ref<Deck | null>(null);
const isDeckArchived = computed(() => !!deck.value?.archivedAt);
const flashcards = ref<Flashcard[]>([]);
const archivedFlashcards = ref<Flashcard[]>([]);
const allCards = computed(() => isDeckArchived.value ? [...flashcards.value, ...archivedFlashcards.value] : flashcards.value);
const showArchived = ref(false);
const loading = ref(false);
const error = ref('');

const showCreateModal = ref(false);
const showEditModal = ref(false);
const showEditDeckModal = ref(false);
const editingFlashcard = ref<Flashcard | null>(null);
const flashcardQuestion = ref('');
const flashcardAnswer = ref('');
const deckName = ref('');
const saving = ref(false);

const expandedCards = ref<Set<string>>(new Set());
const showDeckMenu = ref(false);
const dueCount = ref(0);

function toggleDeckMenu() {
  showDeckMenu.value = !showDeckMenu.value;
}

function closeDeckMenu() {
  showDeckMenu.value = false;
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (!target.closest('.deck-menu-container')) {
    closeDeckMenu();
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    if (showEditDeckModal.value) {
      closeEditDeckModal();
    } else if (showEditModal.value) {
      closeModals();
    } else if (showCreateModal.value) {
      closeModals();
    } else if (showDeckMenu.value) {
      closeDeckMenu();
    }
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('keydown', handleKeydown);
});

async function fetchData() {
  loading.value = true;
  error.value = '';
  try {
    const [deckResult, flashcardsResult, archivedResult, dueCountsResult] = await Promise.all([
      trpc.getDeckById.query({ id: deckId.value }),
      trpc.listFlashcardsByDeck.query({ deckId: deckId.value }),
      trpc.listArchivedFlashcards.query({ deckId: deckId.value }),
      trpc.getDueCountsByDeck.query(),
    ]);
    deck.value = deckResult;
    flashcards.value = flashcardsResult;
    archivedFlashcards.value = archivedResult;
    const deckDueCount = dueCountsResult.find((d) => d.deckId === deckId.value);
    dueCount.value = deckDueCount?.count ?? 0;
    if (!deckResult) {
      error.value = 'Deck not found';
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to fetch data';
  } finally {
    loading.value = false;
  }
}

function toggleCard(id: string) {
  if (expandedCards.value.has(id)) {
    expandedCards.value.delete(id);
  } else {
    expandedCards.value.add(id);
  }
}

function openCreateModal() {
  flashcardQuestion.value = '';
  flashcardAnswer.value = '';
  showCreateModal.value = true;
}

function openEditModal(flashcard: Flashcard) {
  editingFlashcard.value = flashcard;
  flashcardQuestion.value = flashcard.question;
  flashcardAnswer.value = flashcard.answer;
  showEditModal.value = true;
}

function closeModals() {
  showCreateModal.value = false;
  showEditModal.value = false;
  editingFlashcard.value = null;
  flashcardQuestion.value = '';
  flashcardAnswer.value = '';
}

async function createFlashcard() {
  if (!flashcardQuestion.value.trim() || !flashcardAnswer.value.trim()) return;
  saving.value = true;
  try {
    await trpc.createFlashcards.mutate({
      deckId: deckId.value,
      flashcards: [{ question: flashcardQuestion.value.trim(), answer: flashcardAnswer.value.trim() }],
    });
    closeModals();
    await fetchData();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to create flashcard';
  } finally {
    saving.value = false;
  }
}

async function updateFlashcard() {
  if (!editingFlashcard.value || !flashcardQuestion.value.trim() || !flashcardAnswer.value.trim()) return;
  saving.value = true;
  try {
    await trpc.updateFlashcard.mutate({
      idOrShortId: editingFlashcard.value.id,
      question: flashcardQuestion.value.trim(),
      answer: flashcardAnswer.value.trim(),
    });
    closeModals();
    await fetchData();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to update flashcard';
  } finally {
    saving.value = false;
  }
}

async function deleteFlashcard(flashcard: Flashcard) {
  if (!confirm('Are you sure you want to delete this flashcard?')) {
    return;
  }
  try {
    await trpc.deleteFlashcards.mutate({ ids: [flashcard.id] });
    await fetchData();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to delete flashcard';
  }
}

async function archiveFlashcard(flashcard: Flashcard) {
  try {
    await trpc.archiveFlashcards.mutate({ ids: [flashcard.id] });
    await fetchData();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to archive flashcard';
  }
}

async function unarchiveFlashcard(flashcard: Flashcard) {
  try {
    await trpc.unarchiveFlashcards.mutate({ ids: [flashcard.id] });
    await fetchData();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to unarchive flashcard';
  }
}

function openEditDeckModal() {
  if (!deck.value) return;
  deckName.value = deck.value.name;
  showEditDeckModal.value = true;
}

function closeEditDeckModal() {
  showEditDeckModal.value = false;
  deckName.value = '';
}

async function updateDeck() {
  if (!deck.value || !deckName.value.trim()) return;
  saving.value = true;
  try {
    await trpc.updateDeck.mutate({
      id: deck.value.id,
      name: deckName.value.trim(),
    });
    closeEditDeckModal();
    await fetchData();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to update deck';
  } finally {
    saving.value = false;
  }
}

async function archiveDeck() {
  if (!deck.value) return;
  try {
    await trpc.archiveDeck.mutate({ id: deck.value.id });
    router.push('/decks');
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to archive deck';
  }
}

async function unarchiveDeck() {
  if (!deck.value) return;
  try {
    await trpc.unarchiveDeck.mutate({ id: deck.value.id });
    await fetchData();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to unarchive deck';
  }
}

async function deleteDeck() {
  if (!deck.value) return;
  if (!confirm(`Are you sure you want to delete "${deck.value.name}"? This will also delete all flashcards in this deck.`)) {
    return;
  }
  try {
    await trpc.deleteDeck.mutate({ id: deck.value.id });
    router.push('/decks');
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to delete deck';
  }
}

function goBack() {
  router.push('/decks');
}

onMounted(() => {
  if (!isLoading.value && !user.value) {
    router.push('/sign-in');
  } else if (user.value) {
    fetchData();
  }
});

watch(user, (newUser) => {
  if (newUser) {
    fetchData();
  }
});

watch(deckId, () => {
  if (user.value) {
    fetchData();
  }
});
</script>

<template>
  <div class="min-h-screen bg-background">
    <AppHeader />

    <main class="max-w-4xl mx-auto px-4 py-8">
      <div
        v-if="isLoading"
        class="text-center text-muted-foreground"
      >
        Loading...
      </div>

      <div
        v-else-if="user"
        class="space-y-6"
      >
        <div class="flex items-center gap-4">
          <Button
            variant="ghost"
            @click="goBack"
          >
            &larr; Back to Decks
          </Button>
        </div>

        <div
          v-if="loading"
          class="text-center text-muted-foreground"
        >
          Loading...
        </div>

        <div
          v-else-if="deck"
          class="space-y-6"
        >
          <div class="flex justify-between items-center">
            <h2 class="text-2xl font-semibold flex items-center gap-2">
              {{ deck.name }}
              <span
                v-if="deck.isDefault"
                class="text-sm bg-muted px-2 py-0.5 rounded text-muted-foreground font-normal"
              >
                Default
              </span>
              <span
                v-if="deck.archivedAt"
                class="text-sm bg-muted px-2 py-0.5 rounded text-muted-foreground font-normal"
              >
                Archived
              </span>
            </h2>
            <div class="flex items-center gap-2">
              <Button
                v-if="!deck.archivedAt && dueCount > 0"
                @click="router.push({ path: '/review', query: { deckId: deck.id } })"
              >
                Review ({{ dueCount }})
              </Button>
              <Button
                v-if="!deck.archivedAt"
                variant="outline"
                @click="openCreateModal"
              >
                New Flashcard
              </Button>
              <div
                v-if="!deck.isDefault"
                class="relative deck-menu-container"
              >
                <Button
                  variant="outline"
                  class="px-2"
                  @click="toggleDeckMenu"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle cx="12" cy="12" r="1" />
                    <circle cx="12" cy="5" r="1" />
                    <circle cx="12" cy="19" r="1" />
                  </svg>
                </Button>
                <div
                  v-if="showDeckMenu"
                  class="absolute right-0 top-full mt-1 bg-card border border-border rounded-md shadow-lg z-10 min-w-32"
                >
                  <button
                    class="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors cursor-pointer"
                    @click="openEditDeckModal(); closeDeckMenu()"
                  >
                    Edit
                  </button>
                  <button
                    v-if="!deck.archivedAt"
                    class="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors cursor-pointer"
                    @click="archiveDeck(); closeDeckMenu()"
                  >
                    Archive
                  </button>
                  <button
                    v-if="deck.archivedAt"
                    class="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors cursor-pointer"
                    @click="unarchiveDeck(); closeDeckMenu()"
                  >
                    Unarchive
                  </button>
                  <button
                    class="w-full px-4 py-2 text-left text-sm text-destructive hover:bg-muted transition-colors cursor-pointer"
                    @click="deleteDeck(); closeDeckMenu()"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="error"
            class="bg-destructive/10 text-destructive p-4 rounded-md"
          >
            {{ error }}
          </div>

          <div
            v-if="allCards.length === 0"
            class="text-center text-muted-foreground py-8"
          >
            No flashcards in this deck yet. Create your first flashcard!
          </div>

          <div
            v-else
            class="grid gap-4"
          >
            <div
              v-for="flashcard in allCards"
              :key="flashcard.id"
              class="bg-card rounded-lg shadow-md overflow-hidden border border-border"
            >
              <div
                class="p-4 cursor-pointer"
                @click="toggleCard(flashcard.id)"
              >
                <div class="flex justify-between items-start">
                  <div class="flex-1">
                    <p class="font-medium text-foreground">
                      {{ flashcard.question }}
                    </p>
                    <p
                      v-if="expandedCards.has(flashcard.id)"
                      class="mt-3 pt-3 border-t border-border text-muted-foreground"
                    >
                      {{ flashcard.answer }}
                    </p>
                    <p
                      v-else
                      class="mt-2 text-sm text-muted-foreground"
                    >
                      Click to reveal answer
                    </p>
                  </div>
                  <div
                    v-if="!isDeckArchived"
                    class="flex gap-2 ml-4"
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      @click.stop="openEditModal(flashcard)"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      @click.stop="archiveFlashcard(flashcard)"
                    >
                      Archive
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      class="text-destructive hover:text-destructive"
                      @click.stop="deleteFlashcard(flashcard)"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Archived Section -->
          <div
            v-if="!isDeckArchived && archivedFlashcards.length > 0"
            class="mt-8"
          >
            <button
              class="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              @click="showArchived = !showArchived"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                :class="{ 'rotate-90': showArchived }"
                class="transition-transform"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
              <span class="text-sm font-medium">
                Archived ({{ archivedFlashcards.length }})
              </span>
            </button>

            <div
              v-if="showArchived"
              class="mt-4 grid gap-4"
            >
              <div
                v-for="flashcard in archivedFlashcards"
                :key="flashcard.id"
                class="bg-card rounded-lg shadow-md overflow-hidden border border-border opacity-75"
              >
                <div
                  class="p-4 cursor-pointer"
                  @click="toggleCard(flashcard.id)"
                >
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <p class="font-medium text-foreground">
                        {{ flashcard.question }}
                      </p>
                      <p
                        v-if="expandedCards.has(flashcard.id)"
                        class="mt-3 pt-3 border-t border-border text-muted-foreground"
                      >
                        {{ flashcard.answer }}
                      </p>
                      <p
                        v-else
                        class="mt-2 text-sm text-muted-foreground"
                      >
                        Click to reveal answer
                      </p>
                    </div>
                    <div class="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        @click.stop="openEditModal(flashcard)"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        @click.stop="unarchiveFlashcard(flashcard)"
                      >
                        Unarchive
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        class="text-destructive hover:text-destructive"
                        @click.stop="deleteFlashcard(flashcard)"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else-if="error"
          class="text-center text-destructive"
        >
          {{ error }}
        </div>
      </div>

      <div
        v-else
        class="text-center"
      >
        <p class="text-muted-foreground mb-4">
          You need to sign in to access this page.
        </p>
        <Button @click="router.push('/sign-in')">
          Sign In
        </Button>
      </div>
    </main>

    <!-- Create Modal -->
    <div
      v-if="showCreateModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
    >
      <div class="bg-card rounded-lg shadow-xl p-6 w-full max-w-lg border border-border">
        <h3 class="text-lg font-semibold mb-4">
          Create Flashcard
        </h3>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-foreground mb-1">Question</label>
            <textarea
              v-model="flashcardQuestion"
              placeholder="Enter your question"
              rows="3"
              class="w-full px-3 py-2 border border-input rounded-md bg-background"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-foreground mb-1">Answer</label>
            <textarea
              v-model="flashcardAnswer"
              placeholder="Enter the answer"
              rows="3"
              class="w-full px-3 py-2 border border-input rounded-md bg-background"
            />
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <Button
            variant="ghost"
            @click="closeModals"
          >
            Cancel
          </Button>
          <Button
            :disabled="saving || !flashcardQuestion.trim() || !flashcardAnswer.trim()"
            @click="createFlashcard"
          >
            {{ saving ? 'Creating...' : 'Create' }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <div
      v-if="showEditModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
    >
      <div class="bg-card rounded-lg shadow-xl p-6 w-full max-w-lg border border-border">
        <h3 class="text-lg font-semibold mb-4">
          Edit Flashcard
        </h3>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-foreground mb-1">Question</label>
            <textarea
              v-model="flashcardQuestion"
              placeholder="Enter your question"
              rows="3"
              class="w-full px-3 py-2 border border-input rounded-md bg-background"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-foreground mb-1">Answer</label>
            <textarea
              v-model="flashcardAnswer"
              placeholder="Enter the answer"
              rows="3"
              class="w-full px-3 py-2 border border-input rounded-md bg-background"
            />
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <Button
            variant="ghost"
            @click="closeModals"
          >
            Cancel
          </Button>
          <Button
            :disabled="saving || !flashcardQuestion.trim() || !flashcardAnswer.trim()"
            @click="updateFlashcard"
          >
            {{ saving ? 'Saving...' : 'Save' }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Edit Deck Modal -->
    <div
      v-if="showEditDeckModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
    >
      <div class="bg-card rounded-lg shadow-xl p-6 w-full max-w-md border border-border">
        <h3 class="text-lg font-semibold mb-4">
          Edit Deck
        </h3>
        <input
          v-model="deckName"
          type="text"
          placeholder="Deck name"
          class="w-full px-3 py-2 border border-input rounded-md mb-4 bg-background"
          @keyup.enter="updateDeck"
        >
        <div class="flex justify-end gap-2">
          <Button
            variant="ghost"
            @click="closeEditDeckModal"
          >
            Cancel
          </Button>
          <Button
            :disabled="saving || !deckName.trim()"
            @click="updateDeck"
          >
            {{ saving ? 'Saving...' : 'Save' }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
